import { useState } from 'react';
import { IonContent, IonPage, IonSpinner } from '@ionic/react';
import { useIonRouter } from '@ionic/react';
import { loginWithEmailAndPassword, sendAccessCode, loginWithCode } from '../services/authService';
import { useAuthStore } from '../store/useAuthStore';
import revLogo from '../assets/REV_logo.svg';

export const SignInPage: React.FC = () => {
  const router = useIonRouter();
  const { setAuthenticated } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Access code state
  const [showAccessCode, setShowAccessCode] = useState(false);
  const [accessCodeSending, setAccessCodeSending] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await loginWithEmailAndPassword(email, password);
      setAuthenticated(true);
      router.push('/dashboard', 'none');
    } catch {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleSendAccessCode = async () => {
    if (!email) return;
    setAccessCodeSending(true);
    setError('');
    try {
      await sendAccessCode(email);
      setShowAccessCode(true);
      setCodeError('');
      setAccessCode('');
    } catch {
      setError('Failed to send access code');
    } finally {
      setAccessCodeSending(false);
    }
  };

  const handleAccessCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessCode) return;
    setCodeError('');
    setCodeLoading(true);
    try {
      await loginWithCode(email, accessCode);
      setShowAccessCode(false);
      setAuthenticated(true);
      router.push('/dashboard', 'none');
    } catch {
      setCodeError('Invalid access code');
    } finally {
      setCodeLoading(false);
    }
  };

  return (
    <IonPage>
      <IonContent className="ion-padding">
        <div className="flex items-center px-4 py-4">
          <img src={revLogo} alt="REV Logo" className="h-8 w-auto" />
        </div>
        <div className="flex items-center justify-center min-h-full">
          <div className="w-full max-w-sm">
            <h1 className="text-2xl font-bold text-center mb-6">Admin Sign In</h1>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your password"
                />
              </div>
              {error && (
                <p className="text-red-600 text-sm text-center">{error}</p>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <IonSpinner name="crescent" className="w-4 h-4" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
            <div className="mt-3 text-center">
              <button
                type="button"
                onClick={handleSendAccessCode}
                disabled={!email || accessCodeSending}
                className="text-sm text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
              >
                {accessCodeSending ? 'Sending...' : 'Send me an access code (enter your email above)'}
              </button>
            </div>
          </div>
        </div>

        {/* Access Code Modal */}
        {showAccessCode && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowAccessCode(false)} />
            <div className="relative bg-white rounded-xl shadow-lg w-full max-w-sm mx-4 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-2">Enter Access Code</h2>
              <p className="text-sm text-gray-600 mb-4">Check your email for an access code. Enter the code here to login.</p>
              <form onSubmit={handleAccessCodeSubmit} className="flex flex-col gap-4">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-2xl tracking-widest"
                  autoFocus
                />
                {codeError && (
                  <p className="text-red-600 text-sm text-center">{codeError}</p>
                )}
                <div className="flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowAccessCode(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={codeLoading || accessCode.length !== 6}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {codeLoading ? 'Logging in...' : 'Login'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};
